import {
  AutoComplete,
  AutoCompleteMulti,
  FormContainer,
  FormInput,
  ISelectType,
  SwitchInput,
  useFetch,
  useFormContext,
  useNotificationContext,
} from "@4uhub/lib4uhub";
import { Grid } from "@mui/material";
import { useEffect, useRef } from "react";
import { useTranslation } from "react-i18next";
import { useWatch } from "react-hook-form";
import { useParams } from "react-router-dom";
import MainApi from "../../../../../services/mainApi.service";
import { ListImportBatchService } from "../../../../../services/listImportBatch.service";
import {
  convertToNewListSchema,
  TConvertToNewListForm,
} from "./ConvertToNewListSchema";
import { buildListName } from "./buildListName";
import { listCategoriesByProfileAccess, listClassificationsByProfileAccess } from "../../../../../services/listProfileAccess.service";

const translationPath = "page.4uDlist.listFiles.convertToList.createList.";



const GENERIC_ROUTE = process.env.REACT_APP_MAIN + "/api/v1/Generic/";
const genericService = new MainApi<ISelectType>(GENERIC_ROUTE);

const listImportBatchService = new ListImportBatchService();

const userIntegrationTypeIdentifier = { Identifier: "UserIntegrationType" };

const AutoListName = () => {
  const { control, getValues, setValue } =
    useFormContext<TConvertToNewListForm>();
  const classifications = useWatch({ control, name: "listClassifications" });
  const lastGeneratedName = useRef("");

  useEffect(() => {
    const currentName = getValues("name") ?? "";
    const editedByUser = currentName !== "" && currentName !== lastGeneratedName.current;
    if (editedByUser) return;

    const generatedName = buildListName(
      (classifications ?? []).map((classification) => classification.name)
    );
    if (generatedName === currentName) return;

    lastGeneratedName.current = generatedName;
    setValue("name", generatedName, { shouldValidate: generatedName !== "" });
  }, [classifications, getValues, setValue]);

  return null;
};

export const ConvertToNewList = () => {
  const { t } = useTranslation();
  const { listImportBatchId } = useParams();
  const { setMessage } = useNotificationContext();

  const { sendRequest: convert, loading } = useFetch(
    listImportBatchService.convertList
  );

  const saveHandler = (v: TConvertToNewListForm) =>
    convert({
      listImportBatchId: listImportBatchId ?? "",
      name: v.name,
      description: v.description,
      enable: v.enable,
      integrationTypeId: v.integrationType.id,
      listCategoriesIds: v.listCategories.map((c) => c.id),
      listClassificationsIds: v.listClassifications.map((c) => c.id),
    });

  const handleSuccess = () =>
    setMessage({
      message: t("page.4uDlist.listFiles.convertToList.converted"),
      type: "success",
    });

  return (
    <FormContainer<TConvertToNewListForm>
      saveFunction={saveHandler}
      onSuccess={handleSuccess}
      loading={loading}
      title={t(translationPath + "title")}
      schema={convertToNewListSchema}
      subtitle={t(translationPath + "new")}
      subtitleWatchField={"name"}
      defaultValues={{
        enable: true,
        listCategories: [],
        listClassifications: [],
      }}
      fields={() => (
        <Grid container spacing={2}>
          <AutoListName />
          <Grid
            item
            xs={12}
            sm={12}
            sx={{ display: "flex", justifyContent: "flex-end" }}
          >
            <SwitchInput
              name="enable"
              label={t(translationPath + "enable") || "Enable"}
            />
          </Grid>
          <Grid item xs={12} sm={12}>
            <AutoCompleteMulti
              name="listCategories"
              label={
                t(translationPath + "list_categories") || "List Categories"
              }
              getOptionLabel={(option) => option.name}
              request={listCategoriesByProfileAccess}
              size="small"
            />
          </Grid>
          <Grid item xs={12} sm={12}>
            <AutoCompleteMulti
              name="listClassifications"
              label={
                t(translationPath + "list_classifications") ||
                "List Classifications"
              }
              getOptionLabel={(option) => option.name}
              request={listClassificationsByProfileAccess}
              size="small"
            />
          </Grid>
          <Grid item xs={12} sm={4}>
            <AutoComplete
              params={userIntegrationTypeIdentifier}
              name="integrationType"
              label={
                t(translationPath + "integration_type") || "Integration Type"
              }
              getOptionLabel={(option) => option.name}
              fullWidth
              clearIcon={false}
              size="small"
              request={genericService.list}
            />
          </Grid>
          <Grid item xs={12} sm={8}>
            <FormInput
              name="name"
              label={t(translationPath + "name") || "Name"}
              fullWidth
              size="small"
            />
          </Grid>
          <Grid item xs={12} sm={12}>
            <FormInput
              name="description"
              label={t(translationPath + "description") || "Description"}
              fullWidth
              size="small"
              multiline
              minRows={4}
            />
          </Grid>
        </Grid>
      )}
    />
  );
};

export default ConvertToNewList;
